// ============================================
// pages/gunny-bags/GunnyUtilizationList.tsx
// PO-centric fulfillment view. Each PO is a card showing its full story:
// header → fulfillment progress → line items (PACS · bag-type · commodity)
// → consignments (gunny_utilization rows) with the existing action buttons.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Loader2, Eye, Edit2, CheckCircle, XCircle, ShieldCheck, Send,
  ChevronDown, ChevronRight, FileText,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { seasonsAPI, commoditiesAPI, gunnyUtilizationAPI } from '../../api/services';
import apiClient from '../../api/apiClient';
import { UserRole, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';
import { bagTypeLabel } from '../../components/gunny/bagTypes';

interface Consignment {
  id: number;
  status: string;
  bags_supplied: number;
  bags_utilized: number;
  bags_returned: number;
  receipt_verified_at: string | null;
  receipt_rejected_at: string | null;
  receipt_rejection_reason: string | null;
  rejection_reason: string | null;
}
interface DeliveryInvoice { id: number; file_name: string; mime?: string; size_bytes?: number; uploaded_at?: string }
interface DeliverySummary {
  id: number;
  po_id: number;
  bags: number;
  received_bags: number | null;
  received_at: string | null;
  rejected_at: string | null;
  vehicle_number: string | null;
  dispatch_date: string | null;
  supplier_invoice_no: string | null;
  supplier_invoice_date: string | null;
  eway_bill_no: string | null;
  invoices: DeliveryInvoice[];
}
interface LineItem {
  po_item_id: number;
  pacs_id: number | null;
  pacs_name: string | null;
  bag_type: string;
  specification: string | null;
  rate: number;
  commodity_id: number | null;
  commodity_name: string | null;
  delivery_godown_name: string | null;
  ordered: number;
  dispatched: number;
  received: number;
  pending_ack: number;
  deficiency: number;
  remaining: number;
  consignments: Consignment[];
  deliveries: DeliverySummary[];
}
interface PoCard {
  id: number;
  season_id: number;
  po_number: string;
  po_date: string;
  delivery_date: string | null;
  status: string;
  supplier: { id: number; supplier_name: string; tender_id?: string } | null;
  commodity: { id: number; name: string } | null;
  district: { id: number; name: string } | null;
  delivery_godown: { id: number; godown_name: string } | null;
  total_bags: number;
  total_amount: number;
  ordered_total: number;
  dispatched_total: number;
  received_total: number;
  pending_ack_total: number;
  remaining_total: number;
  line_items: LineItem[];
}

const PO_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  draft:            { label: 'Draft',         cls: 'bg-yellow-50 text-yellow-700 border-yellow-300' },
  submitted:        { label: 'Submitted',     cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  dm_verified:      { label: 'DM Verified',   cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  manager_approved: { label: 'Approved',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  fulfilled:        { label: 'Fulfilled',     cls: 'bg-purple-50 text-purple-700 border-purple-300' },
  rejected:         { label: 'Rejected',      cls: 'bg-rose-50 text-rose-700 border-rose-300' },
  closed:           { label: 'Closed',        cls: 'bg-gray-100 text-gray-600 border-gray-300' },
};

const CONSIGNMENT_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  draft:            { label: 'Draft',           cls: 'bg-yellow-50 text-yellow-700' },
  submitted:        { label: 'Submitted',       cls: 'bg-blue-50 text-blue-700' },
  dm_verified:      { label: 'DM Verified',     cls: 'bg-orange-50 text-orange-700' },
  manager_approved: { label: 'MP Approved',     cls: 'bg-emerald-50 text-emerald-700' },
  cao_approved:     { label: 'CAO Approved',    cls: 'bg-teal-50 text-teal-700' },
  md_approved:      { label: 'MD Approved (paid-ready)', cls: 'bg-purple-50 text-purple-700' },
  rejected:         { label: 'Rejected',        cls: 'bg-rose-50 text-rose-700' },
};

export const GunnyUtilizationList: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canSubmit = hasRole(UserRole.DM, UserRole.SUPER_ADMIN);
  const canVerify = hasRole(UserRole.DM, UserRole.SUPER_ADMIN);
  const canApprove = hasRole(UserRole.AO_CAO, UserRole.MD, UserRole.SUPER_ADMIN);
  // Post-MP gates
  const canMpApprove  = hasRole(UserRole.MANAGER_PROCUREMENT, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canCaoApprove = hasRole(UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canMdApprove  = hasRole(UserRole.MD, UserRole.SUPER_ADMIN);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [pos, setPos] = useState<PoCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([seasonsAPI.list(), commoditiesAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setCommodities(Array.isArray(c.data) ? c.data : []);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, []);

  const load = async (sid: number) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await gunnyUtilizationAPI.listByPo(sid);
      setPos(data as PoCard[]);
      // Auto-expand POs with at least one consignment row, so DM sees actionable items first.
      const next = new Set<number>();
      (data as PoCard[]).forEach((p) => {
        if (p.line_items.some((it) => it.consignments.length > 0)) next.add(p.id);
      });
      setExpanded(next);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load fulfillment data');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedSeasonId) load(selectedSeasonId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeasonId]);

  const filteredPos = useMemo(() => {
    const s = search.trim().toLowerCase();
    return pos.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (commodityFilter > 0) {
        const has = p.commodity?.id === commodityFilter
          || p.line_items.some((it) => it.commodity_id === commodityFilter);
        if (!has) return false;
      }
      if (s) {
        const blob = `${p.po_number} ${p.supplier?.supplier_name || ''} ${p.line_items.map((it) => it.pacs_name).join(' ')}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [pos, statusFilter, commodityFilter, search]);

  const toggle = (id: number) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const refreshAfterAction = async () => {
    if (selectedSeasonId) await load(selectedSeasonId);
  };

  // Actions wrap the existing API endpoints. Per-row, contextualized.
  const doSubmit = async (cid: number) => {
    if (!window.confirm('Submit this consignment for DM verification?')) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.submit(selectedSeasonId, cid); await refreshAfterAction(); setMessage({ type: 'success', text: 'Submitted' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Submit failed' }); }
    finally { setBusy(null); }
  };
  const doVerify = async (cid: number) => {
    if (!window.confirm('Verify as DM?')) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.verify(selectedSeasonId, cid, {}); await refreshAfterAction(); setMessage({ type: 'success', text: 'DM verified' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Verify failed' }); }
    finally { setBusy(null); }
  };
  const doApprove = async (cid: number) => {
    if (!window.confirm('Approve as Manager Procurement?')) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.approve(selectedSeasonId, cid, {}); await refreshAfterAction(); setMessage({ type: 'success', text: 'MP approved' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Approve failed' }); }
    finally { setBusy(null); }
  };
  const doCaoApprove = async (cid: number) => {
    if (!window.confirm('Approve as CAO?')) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.caoApprove(selectedSeasonId, cid); await refreshAfterAction(); setMessage({ type: 'success', text: 'CAO approved' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'CAO approve failed' }); }
    finally { setBusy(null); }
  };
  const doMdApprove = async (cid: number) => {
    if (!window.confirm('MD final approval — payment will be unlocked. Continue?')) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.mdApprove(selectedSeasonId, cid); await refreshAfterAction(); setMessage({ type: 'success', text: 'MD approved — payment unlocked' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'MD approve failed' }); }
    finally { setBusy(null); }
  };
  const doReject = async (cid: number) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason?.trim()) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.reject(selectedSeasonId, cid, reason.trim()); await refreshAfterAction(); setMessage({ type: 'success', text: 'Rejected' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Reject failed' }); }
    finally { setBusy(null); }
  };
  const doVerifyReceipt = async (cid: number) => {
    if (!window.confirm('Verify the bags received? Unlocks the row for utilize/return.')) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.verifyReceipt(selectedSeasonId, cid); await refreshAfterAction(); setMessage({ type: 'success', text: 'Receipt verified' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Verify receipt failed' }); }
    finally { setBusy(null); }
  };
  const doRejectReceipt = async (cid: number) => {
    const reason = window.prompt('Why is the receipt being rejected?');
    if (!reason?.trim()) return;
    setBusy(cid); setMessage(null);
    try { await gunnyUtilizationAPI.rejectReceipt(selectedSeasonId, cid, reason.trim()); await refreshAfterAction(); setMessage({ type: 'success', text: 'Receipt rejected — sent back to society' }); }
    catch (e: any) { setMessage({ type: 'error', text: e?.response?.data?.message || 'Reject receipt failed' }); }
    finally { setBusy(null); }
  };

  // DM records utilization on behalf of society. Same societyConfirm endpoint
  // (now opened to DM/AO_CAO) — saves bags_utilized + bags_returned + condition.
  const [recordEdit, setRecordEdit] = useState<{ id: number; supplied: number; utilized: number; returned: number; condition: string } | null>(null);
  const [recordSaving, setRecordSaving] = useState(false);
  const openRecord = (c: Consignment) => setRecordEdit({
    id: c.id, supplied: c.bags_supplied, utilized: c.bags_utilized, returned: c.bags_returned, condition: '',
  });
  const saveRecord = async () => {
    if (!recordEdit) return;
    if (recordEdit.utilized + recordEdit.returned > recordEdit.supplied) {
      setMessage({ type: 'error', text: 'Utilized + returned exceeds supplied' });
      return;
    }
    setRecordSaving(true);
    try {
      await gunnyUtilizationAPI.societyConfirm(selectedSeasonId, recordEdit.id, {
        bags_utilized: recordEdit.utilized,
        bags_returned: recordEdit.returned,
        condition_society: recordEdit.condition || undefined,
      });
      setRecordEdit(null);
      await refreshAfterAction();
      setMessage({ type: 'success', text: 'Recorded' });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Record failed' });
    } finally { setRecordSaving(false); }
  };

  // Bulk forward every ready consignment of a PO into MP/CAO review.
  const forwardAll = async (poId: number, poNumber: string) => {
    if (!window.confirm(`Forward all ready consignments under ${poNumber} to MP/CAO review?`)) return;
    setBusy(poId);
    try {
      const { data } = await gunnyUtilizationAPI.forwardAllForPo(selectedSeasonId, poId);
      const sk = data.skipped.length > 0 ? ` Skipped ${data.skipped.length} (see audit / status sub-line).` : '';
      setMessage({ type: 'success', text: `Forwarded ${data.submitted.length} of ${data.total} consignments.${sk}` });
      await refreshAfterAction();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Forward failed' });
    } finally { setBusy(null); }
  };

  const downloadInvoice = async (poId: number, deliveryId: number, invoiceId: number, filename: string) => {
    try {
      const res = await apiClient.get(
        `/gunny-pos/${selectedSeasonId}/${poId}/deliveries/${deliveryId}/invoices/${invoiceId}`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Could not download invoice' });
    }
  };

  if (loading && pos.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="w-6 h-6 text-amber-600" /> Gunny Utilization
          </h1>
          <p className="text-sm text-gray-500 mt-1">Fulfilment view — every PO with its line items, dispatch progress, and consignment status.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
        <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}{s.is_active ? ' (active)' : ''}</option>))}
        </select>
        <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value) || 0)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value={0}>All commodities</option>
          {commodities.map((c: any) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value="all">All PO statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="manager_approved">Approved</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="closed">Closed</option>
          <option value="rejected">Rejected</option>
        </select>
        <input type="text" placeholder="Search PO no / supplier / PACS…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded flex-1 min-w-[180px]" />
        <span className="text-xs text-gray-500">{filteredPos.length} of {pos.length} POs</span>
      </div>

      {error && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><XCircle className="w-4 h-4" /> {error}</div>}
      {message && (
        <div className={`mb-3 flex items-center gap-2 p-3 rounded text-sm ${
          message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {message.text}
        </div>
      )}

      {/* PO cards */}
      {filteredPos.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          {pos.length === 0 ? 'No POs in this season yet.' : 'No POs match the current filters.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPos.map((p) => {
            const cfg = PO_STATUS_LABEL[p.status] || { label: p.status, cls: 'bg-gray-100 text-gray-600 border-gray-300' };
            const isOpen = expanded.has(p.id);
            const dispatchedPct = p.ordered_total > 0 ? Math.round((p.dispatched_total / p.ordered_total) * 100) : 0;
            const receivedPct = p.ordered_total > 0 ? Math.round((p.received_total / p.ordered_total) * 100) : 0;
            const commodityChip = p.commodity?.name
              ? <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">{p.commodity.name}</span>
              : (() => {
                  const names = Array.from(new Set(p.line_items.map((it) => it.commodity_name).filter(Boolean))) as string[];
                  if (names.length === 1) return <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">{names[0]}</span>;
                  if (names.length > 1) return <span title={names.join(' · ')} className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">mixed ({names.length})</span>;
                  return <span className="text-xs text-gray-400">—</span>;
                })();
            return (
              <div key={p.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header row */}
                <button onClick={() => toggle(p.id)}
                  className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-gray-50">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
                  <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="font-mono font-semibold text-sm">{p.po_number}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.cls}`}>{cfg.label}</span>
                  {commodityChip}
                  <span className="text-xs text-gray-500">{p.supplier?.supplier_name || '—'}</span>
                  <span className="ml-auto text-xs text-gray-500 tabular-nums">{p.po_date}</span>
                </button>

                {/* Fulfillment bar (always visible, even when collapsed) */}
                <div className="px-5 pb-3">
                  <div className="flex items-baseline gap-4 text-xs text-gray-600">
                    <span><span className="text-gray-400">Ordered</span> <span className="font-medium tabular-nums text-gray-900">{p.ordered_total.toLocaleString()}</span></span>
                    <span><span className="text-gray-400">Dispatched</span> <span className="font-medium tabular-nums text-gray-900">{p.dispatched_total.toLocaleString()}</span> <span className="text-gray-400">({dispatchedPct}%)</span></span>
                    <span><span className="text-gray-400">Received</span> <span className="font-medium tabular-nums text-emerald-700">{p.received_total.toLocaleString()}</span> <span className="text-gray-400">({receivedPct}%)</span></span>
                    <span><span className="text-gray-400">Remaining</span> <span className={`font-medium tabular-nums ${p.remaining_total === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{p.remaining_total.toLocaleString()}</span></span>
                    {/* Forward all visible to DM when at least one consignment is ready */}
                    {(canVerify || canApprove) && (() => {
                      const ready = p.line_items.flatMap((it) => it.consignments).filter((c) =>
                        c.status === 'draft' && !!c.receipt_verified_at && (c.bags_utilized + c.bags_returned > 0)
                      );
                      if (ready.length === 0) return null;
                      return (
                        <button onClick={() => forwardAll(p.id, p.po_number)} disabled={busy === p.id}
                          title="Submit every ready consignment under this PO to MP/CAO review"
                          className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50">
                          <Send className="w-3 h-3" /> Forward all ({ready.length})
                        </button>
                      );
                    })()}
                  </div>
                  {/* Stacked progress bar: emerald = received, amber = pending-ack, gray = remaining */}
                  <div className="mt-2 w-full h-2 bg-gray-100 rounded overflow-hidden flex">
                    <div className="bg-emerald-500 h-full" style={{ width: `${receivedPct}%` }} />
                    <div className="bg-amber-400 h-full" style={{ width: `${p.ordered_total > 0 ? Math.round((p.pending_ack_total / p.ordered_total) * 100) : 0}%` }} />
                  </div>
                </div>

                {/* Line items + consignments */}
                {isOpen && (
                  <div className="border-t bg-gray-50/40 px-5 py-3">
                    {p.line_items.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">No line items.</p>
                    ) : (
                      <div className="space-y-3">
                        {p.line_items.map((it) => {
                          const itDispPct = it.ordered > 0 ? Math.round((it.dispatched / it.ordered) * 100) : 0;
                          const itRecvPct = it.ordered > 0 ? Math.round((it.received / it.ordered) * 100) : 0;
                          return (
                            <div key={it.po_item_id} className="bg-white border border-gray-200 rounded-lg p-3">
                              {/* Line header */}
                              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                                <span className="font-medium text-gray-900">{it.pacs_name || <span className="italic text-gray-400">no PACS — Allot first</span>}</span>
                                {it.commodity_name && (
                                  <span className="inline-block px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-700 rounded">{it.commodity_name}</span>
                                )}
                                <span className="text-xs text-gray-700">{bagTypeLabel(it.bag_type)}</span>
                                {it.specification && <span className="text-xs text-gray-500">· {it.specification}</span>}
                                {it.delivery_godown_name && <span className="text-[10px] text-gray-500">→ {it.delivery_godown_name}</span>}
                                {it.rate > 0 && <span className="text-[10px] text-gray-500">· ₹ {it.rate.toFixed(2)}/bag</span>}
                              </div>
                              {/* Per-line numbers */}
                              <div className="flex flex-wrap items-baseline gap-3 text-xs text-gray-600 mb-1">
                                <span><span className="text-gray-400">Ordered</span> <span className="tabular-nums font-medium text-gray-900">{it.ordered.toLocaleString()}</span></span>
                                <span><span className="text-gray-400">Dispatched</span> <span className="tabular-nums">{it.dispatched.toLocaleString()}</span> <span className="text-gray-400">({itDispPct}%)</span></span>
                                <span><span className="text-gray-400">Received</span> <span className="tabular-nums text-emerald-700">{it.received.toLocaleString()}</span> <span className="text-gray-400">({itRecvPct}%)</span></span>
                                {it.deficiency > 0 && <span className="text-amber-700">Short by {it.deficiency.toLocaleString()}</span>}
                                <span><span className="text-gray-400">Remaining</span> <span className={`tabular-nums ${it.remaining === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{it.remaining.toLocaleString()}</span></span>
                              </div>

                              {/* Supplier-provided invoices for this line item — clickable for download. */}
                              {it.deliveries.some((d) => d.invoices.length > 0 || d.supplier_invoice_no) && (
                                <div className="mt-2 mb-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                                  <span className="text-gray-400">Supplier invoices:</span>
                                  {it.deliveries.flatMap((d) => [
                                    ...(d.supplier_invoice_no ? [
                                      <span key={`n-${d.id}`} title={`Vehicle ${d.vehicle_number || '—'} · Inv-date ${d.supplier_invoice_date || '—'} · E-way ${d.eway_bill_no || '—'}`}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded font-mono text-[10px]">
                                        {d.supplier_invoice_no}
                                      </span>
                                    ] : []),
                                    ...d.invoices.map((inv) => (
                                      <button key={inv.id} onClick={() => downloadInvoice(d.po_id, d.id, inv.id, inv.file_name)}
                                        title={`Download ${inv.file_name}`}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded">
                                        📎 {inv.file_name.length > 22 ? inv.file_name.slice(0, 19) + '…' : inv.file_name}
                                      </button>
                                    )),
                                  ])}
                                </div>
                              )}

                              {/* Consignments */}
                              {it.consignments.length === 0 ? (
                                <div className="text-[11px] text-gray-500 italic mt-2">No consignment yet — society hasn't acknowledged any dispatch.</div>
                              ) : (
                                <div className="mt-2 border-t pt-2">
                                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Consignments at PACS</div>
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                      <thead>
                                        <tr className="text-left text-gray-500">
                                          <th className="pr-3 py-1">#</th>
                                          <th className="pr-3 py-1">Status</th>
                                          <th className="pr-3 py-1">Receipt</th>
                                          <th className="pr-3 py-1 text-right">Supplied</th>
                                          <th className="pr-3 py-1 text-right">Utilized</th>
                                          <th className="pr-3 py-1 text-right">Returned</th>
                                          <th className="pr-3 py-1 text-right">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {it.consignments.map((c) => {
                                          const cs = CONSIGNMENT_STATUS_LABEL[c.status] || { label: c.status, cls: 'bg-gray-100 text-gray-500' };
                                          const loadingRow = busy === c.id;
                                          const receiptBadge = c.status === 'draft'
                                            ? (c.receipt_verified_at
                                                ? <span className="text-emerald-700 text-[10px] flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" /> verified</span>
                                                : c.receipt_rejected_at
                                                  ? <span title={c.receipt_rejection_reason || ''} className="text-rose-600 text-[10px]">rejected</span>
                                                  : <span className="text-amber-600 text-[10px]">awaiting DM</span>)
                                            : <span className="text-gray-300 text-[10px]">—</span>;
                                          return (
                                            <tr key={c.id} className="border-t border-gray-100 align-middle">
                                              <td className="pr-3 py-1.5 text-gray-500">#{c.id}</td>
                                              <td className="pr-3 py-1.5">
                                                <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${cs.cls}`}>{cs.label}</span>
                                              </td>
                                              <td className="pr-3 py-1.5">{receiptBadge}</td>
                                              <td className="pr-3 py-1.5 text-right tabular-nums">{c.bags_supplied.toLocaleString()}</td>
                                              <td className="pr-3 py-1.5 text-right tabular-nums">{c.bags_utilized.toLocaleString()}</td>
                                              <td className="pr-3 py-1.5 text-right tabular-nums">{c.bags_returned.toLocaleString()}</td>
                                              <td className="pr-3 py-1.5 text-right whitespace-nowrap">
                                                <button onClick={() => navigate(`/gunny-bags/utilization/${c.id}`)}
                                                  title="View / Edit" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-700 rounded mr-1">
                                                  <Eye className="w-3 h-3" /> View
                                                </button>
                                                {c.status === 'draft' && canVerify && !c.receipt_verified_at && (
                                                  <>
                                                    <button onClick={() => doVerifyReceipt(c.id)} disabled={loadingRow}
                                                      title="Verify the bags received"
                                                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white rounded mr-1 disabled:opacity-50">
                                                      <ShieldCheck className="w-3 h-3" /> Verify receipt
                                                    </button>
                                                    {!c.receipt_rejected_at && (
                                                      <button onClick={() => doRejectReceipt(c.id)} disabled={loadingRow}
                                                        title="Send back to society for correction"
                                                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-rose-600 hover:bg-rose-700 text-white rounded mr-1 disabled:opacity-50">
                                                        <XCircle className="w-3 h-3" /> Reject receipt
                                                      </button>
                                                    )}
                                                  </>
                                                )}
                                                {c.status === 'draft' && (canVerify || canApprove) && c.receipt_verified_at && (
                                                  <button onClick={() => openRecord(c)} disabled={loadingRow}
                                                    title="DM records bags utilized / returned on behalf of PACS"
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white rounded mr-1 disabled:opacity-50">
                                                    <Edit2 className="w-3 h-3" /> Record bags
                                                  </button>
                                                )}
                                                {c.status === 'draft' && canSubmit && c.receipt_verified_at && (
                                                  <button onClick={() => navigate(`/gunny-bags/utilization/${c.id}`)} title="Open the full row to edit"
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded mr-1">
                                                    <Edit2 className="w-3 h-3" /> Edit
                                                  </button>
                                                )}
                                                {c.status === 'submitted' && canVerify && (
                                                  <button onClick={() => doVerify(c.id)} disabled={loadingRow}
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-orange-500 hover:bg-orange-600 text-white rounded mr-1 disabled:opacity-50">
                                                    <Send className="w-3 h-3" /> Verify
                                                  </button>
                                                )}
                                                {c.status === 'dm_verified' && canMpApprove && (
                                                  <button onClick={() => doApprove(c.id)} disabled={loadingRow}
                                                    title="Manager Procurement signs off"
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white rounded mr-1 disabled:opacity-50">
                                                    <CheckCircle className="w-3 h-3" /> MP Approve
                                                  </button>
                                                )}
                                                {c.status === 'manager_approved' && canCaoApprove && (
                                                  <button onClick={() => doCaoApprove(c.id)} disabled={loadingRow}
                                                    title="CAO approves after MP"
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-teal-600 hover:bg-teal-700 text-white rounded mr-1 disabled:opacity-50">
                                                    <CheckCircle className="w-3 h-3" /> CAO Approve
                                                  </button>
                                                )}
                                                {c.status === 'cao_approved' && canMdApprove && (
                                                  <button onClick={() => doMdApprove(c.id)} disabled={loadingRow}
                                                    title="MD final approval — payment unlocks"
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-purple-600 hover:bg-purple-700 text-white rounded mr-1 disabled:opacity-50">
                                                    <CheckCircle className="w-3 h-3" /> MD Approve
                                                  </button>
                                                )}
                                                {['submitted', 'dm_verified', 'manager_approved', 'cao_approved'].includes(c.status) && (canVerify || canApprove || canCaoApprove || canMdApprove) && (
                                                  <button onClick={() => doReject(c.id)} disabled={loadingRow}
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50">
                                                    <XCircle className="w-3 h-3" /> Reject
                                                  </button>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* DM Record bags modal */}
      {recordEdit && (() => {
        const remaining = recordEdit.supplied - recordEdit.utilized - recordEdit.returned;
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="px-6 py-4 border-b">
                <h2 className="font-semibold text-gray-900">Record bags used / returned</h2>
                <p className="text-xs text-gray-500 mt-0.5">DM is recording on behalf of PACS. Out of {recordEdit.supplied.toLocaleString()} supplied, enter the actual usage.</p>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bags utilized</label>
                  <input type="number" min={0} max={recordEdit.supplied} value={recordEdit.utilized}
                    onChange={(e) => setRecordEdit({ ...recordEdit, utilized: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bags returned</label>
                  <input type="number" min={0} max={recordEdit.supplied} value={recordEdit.returned}
                    onChange={(e) => setRecordEdit({ ...recordEdit, returned: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition (optional)</label>
                  <input type="text" value={recordEdit.condition}
                    onChange={(e) => setRecordEdit({ ...recordEdit, condition: e.target.value })}
                    placeholder="e.g. Good Condition (New B-twill)"
                    className="w-full px-3 py-2 border rounded text-sm" />
                </div>
                <div className={`text-xs ${remaining < 0 ? 'text-rose-700' : 'text-gray-600'}`}>
                  Unaccounted: <span className="font-medium tabular-nums">{remaining.toLocaleString()}</span>
                  {remaining < 0 && ' — over the supplied count'}
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
                <button onClick={() => setRecordEdit(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
                <button onClick={saveRecord} disabled={recordSaving || remaining < 0}
                  className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded">
                  {recordSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Helper kept around for future use.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _num = num;

export default GunnyUtilizationList;
